import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import cs from 'classnames';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';

import { cn, ia } from 'lib/helpers/utility';
import { useUIContext } from 'lib/context/UIContext/UIContext';
import { usePacketBuilderContext } from 'lib/context/PacketBuilder/PacketBuilderContext';

import Input from 'components/shared/Input/Input';
import ErrorMessage from 'components/shared/ErrorMessage/ErrorMessage';
import ErrorBoundary, { withErrorBoundary } from 'components/shared/Error/Boundary';

import { getCurrentWithoutHidden, getStepsLeft } from '../lib/helpers';

import StepsChart from './StepsChart';
import PacketFormRow from './PacketFormRow';

const Forms = ({ currentBreadcrumb, formsList, formId, handleAddNewForm }) => {
  const { breadCrumbs, setSearchTerm, setEnableSubmit, formik } = usePacketBuilderContext();
  const { device } = useUIContext();
  const navigate = useNavigate();
  const currentBreadcrumbStep = getCurrentWithoutHidden(breadCrumbs, currentBreadcrumb);
  const getBreadcrumbsLeft = getStepsLeft(breadCrumbs, currentBreadcrumb);

  const cacheRef = useRef(new Map());
  useEffect(() => {
    (formsList?.forms || []).forEach((f) => {
      if (f?.id != null) cacheRef.current.set(String(f.id), f);
    });
  }, [formsList]);

  const formIds = formik.values?.form_ids || [];
  useEffect(() => {
    setEnableSubmit(formIds.length > 0);
  }, [formIds.length, setEnableSubmit]);

  const selectedForms = useMemo(
    () => formIds.map((id) => cacheRef.current.get(String(id))).filter(Boolean),
    [formIds]
  );

  const availableForms = useMemo(() => {
    const sel = new Set(formIds.map(String));
    return (formsList?.forms || []).filter((f) => !sel.has(String(f.id)));
  }, [formsList, formIds]);

  const toggle = useCallback(
    (id) => {
      const has = formIds.map(String).includes(String(id));
      if (has) {
        formik.setFieldValue(
          'form_ids',
          formIds.filter((x) => String(x) !== String(id))
        );
      } else {
        formik.setFieldValue('form_ids', [...formIds, id]);
      }
    },
    [formIds, formik]
  );

  const onDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;
      const from = result.source.index;
      const to = result.destination.index;
      if (from === to) return;
      const next = Array.from(formIds);
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      formik.setFieldValue('form_ids', next);
    },
    [formIds, formik]
  );

  const chartData = [
    { name: 'completed', value: currentBreadcrumbStep, fills: '#4ADE80' },
    { name: 'not-completed', value: getBreadcrumbsLeft, fills: '#fff' }
  ];

  const addNewForm = () => {
    if (handleAddNewForm) return handleAddNewForm();
    navigate('/portal/settings/forms', {
      state: { openModal: true, packet_id: formik?.values?.id }
    });
  };

  const selectedCount = selectedForms.length;

  return (
    <ErrorBoundary FallbackComponent={ErrorMessage}>
      <div>
        <StepsChart
          chartData={chartData}
          title="Choose form(s)"
          description="All forms selected will be included in this form packet."
          innerText={`${currentBreadcrumbStep}/${currentBreadcrumbStep + getBreadcrumbsLeft}`}
          showButton
          buttonText="Add new form"
          handleClick={addNewForm}
        />
        <div className="px-9 pt-10">
          <div className="flex items-center justify-between">
            <p className="text-sm font-500 text-primary-900">Add forms to your packet.</p>
            <div className="flex items-center gap-3">
              <p className={cs(selectedCount ? 'text-primary-900' : 'text-neutral-500')}>
                {selectedCount} selected
              </p>
              <Input
                placeholder="Search forms"
                icon="search-linear"
                rounded="full"
                width={device === 'tablet-sm' ? '250px' : '280px'}
                onChange={(e) => setSearchTerm((prev) => ({ ...prev, forms: e.target.value }))}
              />
            </div>
          </div>
          <div className="mt-[21px] flex flex-col gap-2">
            {selectedCount > 0 && (
              <>
                <div className="text-xs font-600 uppercase tracking-wide text-neutral-600">
                  Selected — drag to reorder
                </div>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="packet-selected-forms">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="flex flex-col gap-2">
                        {selectedForms.map((form, index) => (
                          <Draggable
                            key={String(form.id)}
                            draggableId={String(form.id)}
                            index={index}>
                            {(dp, snap) => (
                              <div
                                ref={dp.innerRef}
                                {...dp.draggableProps}
                                style={dp.draggableProps.style}
                                className={cn(snap.isDragging && 'shadow-md')}>
                                <PacketFormRow
                                  form={form}
                                  index={index}
                                  isSelected
                                  dragHandleProps={dp.dragHandleProps}
                                  onToggle={toggle}
                                  formId={formId}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                <div className="mt-3 text-xs font-600 uppercase tracking-wide text-neutral-600">
                  Available
                </div>
              </>
            )}
            {ia(availableForms) ? (
              availableForms.map((form) => (
                <PacketFormRow
                  key={form.id}
                  form={form}
                  isSelected={false}
                  onToggle={toggle}
                  formId={formId}
                />
              ))
            ) : (
              <div className="py-4 text-center text-sm text-neutral-500">
                {selectedCount > 0 ? 'No more forms match your search.' : 'No forms found.'}
              </div>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default withErrorBoundary(Forms);
